import React, { useState } from 'react';
import useTeacher from '../hooks/useTeacher';
import AccountFields from '../../../components/AccountFields';
import { notifyAccountCreated } from '../../../components/notifyAccountCreated';
import { getApiErrorMessage, getApiFieldErrors } from '../../../lib/api-error';

// Các field có dòng lỗi riêng dưới ô; lỗi field khác hiện ở banner
const SHOWN_ERROR_FIELDS = ['email', 'user_name'];

interface CreateTeacherModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string;
  schoolName: string;
}

const CreateTeacherModal: React.FC<CreateTeacherModalProps> = ({ isOpen, onClose, schoolId, schoolName }) => {
  const { createTeacherMutation, isCreating } = useTeacher();
  const emptyForm = () => ({
    name: '',
    email: '',
    phone: '',
    user_name: '',
    unit: '',
    subjects: '',
    classification: 'Giảng viên',
    monthlyQuota: 120
  });
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setFormError('');
    try {
      const created = await createTeacherMutation.mutateAsync({ ...formData, schoolId });
      notifyAccountCreated(`Tạo giảng viên "${formData.name}" thành công!`, created);
      setFormData(emptyForm());
      onClose();
    } catch (error) {
      setErrors(getApiFieldErrors(error));
      setFormError(getApiErrorMessage(error, 'Có lỗi xảy ra khi tạo giảng viên', SHOWN_ERROR_FIELDS));
    }
  };

  const setField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setField(e.target.name, e.target.value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg mr-3">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Tạo giảng viên mới</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">Trường: {schoolName}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{formError}</div>
          )}
          <div className="space-y-4">
            {/* School field - disabled */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trường học
              </label>
              <input
                type="text"
                value={schoolName}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Họ và tên <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Nhập họ và tên giảng viên"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="example@email.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Số điện thoại
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="0901234567"
              />
            </div>

            <AccountFields
              userName={formData.user_name}
              onChange={setField}
              errors={errors}
              disabled={isCreating}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Đơn vị <span className="text-red-500">*</span>
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Chọn đơn vị</option>
                <option value="Chi nhánh Hồ Chí Minh">Chi nhánh Hồ Chí Minh</option>
                <option value="Chi nhánh Hà Nội">Chi nhánh Hà Nội</option>
                <option value="Chi nhánh Đà Nẵng">Chi nhánh Đà Nẵng</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Môn dạy
              </label>
              <input
                type="text"
                name="subjects"
                value={formData.subjects}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Ví dụ: Toán, Lý, Hóa (phân cách bằng dấu phẩy)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phân loại <span className="text-red-500">*</span>
                </label>
                <select
                  name="classification"
                  value={formData.classification}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="Giảng viên">Giảng viên</option>
                  <option value="Kiêm nhiệm">Kiêm nhiệm</option>
                  <option value="Thỉnh giảng">Thỉnh giảng</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quota tháng (giờ)
                </label>
                <input
                  type="number"
                  name="monthlyQuota"
                  value={formData.monthlyQuota}
                  onChange={handleChange}
                  min="0"
                  max="200"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Hủy
            </button>
                         <button
               type="submit"
               disabled={isCreating}
               className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
             >
               {isCreating ? 'Đang tạo...' : 'Tạo giảng viên'}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeacherModal;
